<template>
  <div class="filter">
    <div class="filter__sections">
      <div
        v-for="sec in filterOptions"
        :key="sec.id"
        class="filter__section"
      >
        <header
          class="filter__section-header"
          :aria-expanded="activeSectionId === sec.id"
          :aria-controls="`body-${sec.id}`"
          @click="toggleSection(sec.id)"
        >
          <span class="filter__section-label">
            {{ sec.label }}
            <span
              v-if="activeCount(sec) > 0"
              class="filter__section-count"
            >{{ activeCount(sec) }}</span>
          </span>
          <ChevronDown
            class="filter__section-toggle-icon"
            :class="{ 'is-open': activeSectionId === sec.id }"
          />
        </header>

        <div
          v-show="activeSectionId === sec.id"
          :id="`body-${sec.id}`"
          class="filter__section-body"
        >
          <div
            v-if="sec.useSearchbar"
            class="filter__search"
          >
            <input
              v-model="sec.query"
              type="text"
              class="filter__search-input"
              placeholder="Поиск.."
              aria-label="Поиск по фильтру"
              @focusin="isSearchbarFocused = true"
              @focusout="isSearchbarFocused = false"
              @input="val => getFields(sec.id, val.target.value)"
            >
            <SearchIcon
              class="filter__search-icon"
              :class="{ 'filter__search-icon--active': isSearchbarFocused }"
            />
          </div>
          <ul
            ref="lists"
            :data-sec-id="sec.id"
            class="filter__options-list scroll-gray"
            :class="{ 'filter__options-list--scrolled': overflowMap[sec.id] }"
            @scroll.passive="onScroll(sec.id, $event)"
          >
            <li
              v-for="item in sec.options"
              :key="item.id"
              class="filter__option-item"
              :class="{ 'filter__option-item--active': item.value }"
              @click="item.value = !item.value; emit('update-filter')"
            >
              <Checkbox
                v-model="item.value"
                :label="item.name"
              />
            </li>
          </ul>
        </div>
      </div>

      <!-- Теговый фильтр (не меняли) -->
      <div class="filter__section filter__section-tags">
        <header
          class="filter__section-header"
          :aria-expanded="activeSectionId === filterTagOptions.id"
          :aria-controls="`body-${filterTagOptions.id}`"
          @click="toggleSection(filterTagOptions.id)"
        >
          <span class="filter__section-label">{{ filterTagOptions.label }}</span>
          <ChevronDown
            class="filter__section-toggle-icon"
            :class="{ 'is-open': activeSectionId === filterTagOptions.id }"
          />
        </header>

        <div
          v-show="activeSectionId === filterTagOptions.id"
          :id="`body-${filterTagOptions.id}`"
          class="filter__section-body"
        >
          <div class="filter__search">
            <input
              :value="filterTagOptions.query"
              type="text"
              class="filter__search-input"
              placeholder="Поиск.."
              aria-label="Поиск по фильтру"
              @focusin="isSearchbarFocused = true"
              @focusout="isSearchbarFocused = false"
              @input="val => getFilterTags(val.target.value)"
            >
            <SearchIcon
              class="filter__search-icon"
              :class="{ 'filter__search-icon--active': isSearchbarFocused }"
            />
          </div>
          <ul class="filter__options-list">
            <li
              v-for="(categoryItems, categoryName) in filterTagOptions.options"
              :key="categoryName"
              class="filter__option-category"
            >
              {{ categoryName !== 'null' ? categoryName : 'Без категории' }} ({{ categoryItems.length }})
              <ul class="filter__option-category-list">
                <li
                  v-for="item in categoryItems"
                  :key="item.id"
                  class="filter__option-item"
                  @click="emit('add-filter-tag', item)"
                >
                  <Checkbox
                    :model-value="filterTagOptions.selected.some(s => s.id === item.id)"
                    :label="item.name"
                  />
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Список выбранных тегов -->
    <div
      v-if="filterTagOptions.selected.length"
      class="filter-tags"
    >
      <div
        v-for="tag in filterTagOptions.selected"
        :key="tag.id"
        class="filter-tags__item"
      >
        {{ tag.category }} | {{ tag.name }}
        <CrossIconBold
          class="filter-tags__item-icon"
          @click="emit('add-filter-tag', tag)"
        />
      </div>
    </div>

    <footer class="filter__footer">
      <ButtonAccentTertiary
        :disabled="activeFilterCount === 0"
        class="filter__btn filter__btn--reset"
        @click="activeFilterCount > 0 ? emit('reset-filter') : false"
      >
        Очистить фильтры
      </ButtonAccentTertiary>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, nextTick } from "vue";
import ChevronDown from "@/components/icons/ChevronDown.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import CrossIconBold from "@/components/icons/CrossIconBold.vue";

const emit = defineEmits([
  "apply",
  "reset",
  "update-filter",
  "get-filter-fields",
  "get-filter-tags",
  "add-filter-tag",
  "reset-filter"
]);

const props = defineProps({
  filterOptions: {
    type: Array,
    required: true
  },
  filterTagOptions: {
    type: Object,
    required: true
  },
  activeFilterCount: {
    type: Number,
    default: 0
  }
});

const isSearchbarFocused = ref(false);
const activeSectionId = ref(null);

const overflowMap = reactive({});
const lists = ref([]);

const toggleSection = async (id) => {
  activeSectionId.value = activeSectionId.value === id ? null : id;
  await nextTick();
  const ul = lists.value.find(el => el.dataset.secId === id);
  overflowMap[id] = ul ? ul.scrollHeight > ul.clientHeight : false;
};

const onScroll = (secId, ev) => {
  const el = ev.target;
  overflowMap[secId] = el.scrollHeight > el.clientHeight;
};

const getFields = (index, query) => {
  emit("get-filter-fields", { index, query });
};

const getFilterTags = (query) => {
  emit("get-filter-tags", query);
};

const activeCount = (sec) => {
  return Array.isArray(sec.options)
      ? sec.options.reduce((sum, item) => sum + (item.value ? 1 : 0), 0)
      : 0;
};
</script>

<style scoped lang="scss">
.filter {
  background: #fff;
  border-radius: 8px;
  box-shadow: 1px 2px 10px rgba(19, 30, 39, 0.15);
  display: flex;
  flex-direction: column;
  gap: 20px;
  left: 0;
  overflow: hidden;
  padding: 14px 7px 14px 14px;
  position: absolute;
  top: calc(100% + 10px);
  width: 320px;
  z-index: 2000;

  max-height: 460px;
  @media (min-height: 768px) {
    max-height: 600px;
  }
  @media (min-height: 900px) {
    max-height: 680px;
  }
  @media (min-height: 1080px) {
    max-height: 720px;
  }

  &__sections {
    padding-right: 7px;
  }

  &-tags {
    display: flex;
    gap: 5px;
    cursor: auto;
    flex-wrap: wrap;

    &__item {
      display: flex;
      gap: 4px;
      justify-content: space-between;
      align-items: center;
      border-radius: 20px;
      background: #f7f8fd;
      padding: 4px 12px;
      font-size: 14px;
      font-weight: 400;
      color: #7b87a5;

      &-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 14px;
        height: 14px;
        cursor: pointer;
        user-select: none;
      }
    }
  }

  &__section {
    overflow-x: hidden;

    & + & {
      margin-top: 10px;
    }
  }

  &__section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 4px 0;
  }

  &__section-label {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #7b87a5;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }

  &__section-count {
    align-items: center;
    background: #eef1f4;
    border-radius: 20px;
    color: #1f9aff;
    display: flex;
    font-size: 11px;
    font-weight: 500;
    height: 18px;
    justify-content: center;
    width: 18px;
    line-height: 1;
  }

  &__section-toggle-icon {
    color: #7b87a5;
    transition: transform 0.2s;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__section-body {
    margin-top: 8px;
  }

  &__options-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 4px;
    margin: 0;
    list-style: none;
    max-height: 200px;
    overflow-y: auto;
    background: transparent;
    transition: background 0.2s;

    @media (min-height: 768px) {
      max-height: 300px;
    }
    @media (min-height: 900px) {
      max-height: 350px;
    }
    @media (min-height: 1080px) {
      max-height: 400px;
    }

    &--scrolled {
      border-radius: 8px;
      background: #F7F8FD;
    }
  }

  &__option-category {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 14px;
    font-weight: 500;
    color: #7b87a5;
  }

  &__option-category-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 14px;
    font-weight: 500;
    color: #7b87a5;
    width: 100%;
  }

  &__option-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 14px;
    border-radius: 8px;
    min-height: 30px;
    width: 100%;

    &:hover {
      background: #7b87a51a;
    }

    &--active:hover {
      background: #e0f0ff;
    }
  }

  &__option-label {
    font-size: 14px;
    color: #333;
    user-select: none;
    cursor: pointer;
    font-weight: 400;

    &--active {
      color: #1f9aff;
    }
  }

  &__footer {
    display: flex;
    gap: 10px;
  }

  &__btn {
    width: 100%;
  }

  &__search {
    display: grid;
    grid-template-columns: 90% 10%;
    align-items: center;
    background: #f7f8fd;
    height: 36px;
    color: #373d3f;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 8px;

    &-input {
      width: 100%;
      height: 100%;
      border: none;
      padding: 10px;
      font-size: 14px;
      font-weight: 400;
      background: transparent;
      outline: none;
    }

    &-icon {
      color: #7b87a5;
      transition: color 0.2s;

      &--active {
        color: #1f9aff;
      }
    }
  }
}
</style>
